/**
 * User list + slide-out create/edit panel (admin only).
 * Phase 26b fix: no autofill defaults, admin password management.
 */

import { useState } from 'react';
import useEntityList from '../../hooks/useEntityList';
import { fetchUsers, updateUser, registerUser, setUserPassword } from '../../api/users';
import { SlideOutPanel, Button, LoadingSpinner, EmptyState, Pagination } from '../common';

export default function UserList({ search, showDeleted }) {
  const { items, loading, totalCount, page, setPage, refresh } = useEntityList(fetchUsers, { search, showDeleted });
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({});
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);

  function openCreate() {
    setForm({ email: '', first_name: '', last_name: '', role: 'builder', password: '', is_active: true });
    setNewPassword('');
    setCreating(true);
    setSelected(null);
  }

  function openEdit(item) {
    setForm({
      email: item.email || '',
      first_name: item.first_name || '',
      last_name: item.last_name || '',
      role: item.role || 'builder',
      phone: item.phone || '',
      is_active: item.is_active ?? true,
    });
    setNewPassword('');
    setSelected(item);
    setCreating(false);
  }

  function closePanel() { setSelected(null); setCreating(false); setNewPassword(''); }

  async function handleSave() {
    if (creating && !form.email) { alert('Email is required.'); return; }
    if (creating && !form.password) { alert('Password is required for new users.'); return; }
    if (creating && form.password.length < 6) { alert('Password must be at least 6 characters.'); return; }

    setSaving(true);
    try {
      if (creating) {
        await registerUser(form);
      } else {
        const payload = { ...form };
        delete payload.password;
        await updateUser(selected.id, payload);
      }
      closePanel();
      refresh();
    } catch (err) {
      const data = err.response?.data;
      const msg = data?.error || (typeof data === 'object' ? JSON.stringify(data) : 'Save failed');
      alert(msg);
    } finally {
      setSaving(false);
    }
  }

  async function handleSetPassword() {
    if (!newPassword) { alert('Enter a new password.'); return; }
    if (newPassword.length < 6) { alert('Password must be at least 6 characters.'); return; }
    setPasswordSaving(true);
    try {
      await setUserPassword(selected.id, newPassword);
      setNewPassword('');
      alert('Password updated successfully.');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update password.');
    } finally {
      setPasswordSaving(false);
    }
  }

  if (loading) return <div className="flex justify-center py-10"><LoadingSpinner /></div>;

  return (
    <div>
      <div className="px-4 py-2 border-b border-gray-100">
        <Button size="sm" onClick={openCreate}>+ New User</Button>
      </div>

      {items.length === 0 ? (
        <EmptyState title="No users" description="Register users to manage team access." />
      ) : (
        <>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
                <th className="px-4 py-2">Email</th>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Role</th>
                <th className="px-4 py-2 text-center">Active</th>
                <th className="px-4 py-2 text-right">Last Login</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((item) => (
                <tr
                  key={item.id}
                  onClick={() => openEdit(item)}
                  className={`hover:bg-blue-50 cursor-pointer ${!item.is_active ? 'opacity-50' : ''}`}
                >
                  <td className="px-4 py-2 font-medium text-foreground">{item.email}</td>
                  <td className="px-4 py-2 text-gray-600">
                    {[item.first_name, item.last_name].filter(Boolean).join(' ') || '—'}
                  </td>
                  <td className="px-4 py-2">
                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                      item.role === 'admin' ? 'bg-red-100 text-red-700' :
                      item.role === 'manager' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {item.role}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-center">
                    {item.is_active ? (
                      <span className="text-green-600 text-xs">Active</span>
                    ) : (
                      <span className="text-red-500 text-xs">Inactive</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right text-gray-400 text-xs">{item.last_login?.slice(0, 10) || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-2">
            <Pagination current={page} total={Math.ceil(totalCount / 200)} onChange={setPage} />
          </div>
        </>
      )}

      <SlideOutPanel
        open={!!selected || creating}
        onClose={closePanel}
        title={creating ? 'New User' : `Edit: ${selected?.email || ''}`}
        width="max-w-md"
      >
        <div className="space-y-4" autoComplete="off">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="text"
              name={`nofill_email_${Date.now()}`}
              autoComplete="nope"
              data-lpignore="true"
              data-1p-ignore="true"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              value={form.email || ''}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="user@example.com"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
              <input
                type="text"
                name={`nofill_fname_${Date.now()}`}
                autoComplete="nope"
                data-lpignore="true"
                data-1p-ignore="true"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                value={form.first_name || ''}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                placeholder="First name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
              <input
                type="text"
                name={`nofill_lname_${Date.now()}`}
                autoComplete="nope"
                data-lpignore="true"
                data-1p-ignore="true"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                value={form.last_name || ''}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                placeholder="Last name"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              value={form.role || 'builder'}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
            >
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
              <option value="builder">Builder</option>
              <option value="viewer">Viewer</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input
              type="text"
              autoComplete="off"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              value={form.phone || ''}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="Phone number"
            />
          </div>

          {/* Password — shown on create, separate change section on edit */}
          {creating ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                type="text"
                name={`nofill_pw_${Date.now()}`}
                autoComplete="nope"
                data-lpignore="true"
                data-1p-ignore="true"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                value={form.password || ''}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Min 6 characters"
                style={{ WebkitTextSecurity: 'disc' }}
              />
            </div>
          ) : (
            <div className="border-t border-gray-200 pt-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Change Password</label>
              <div className="flex items-end gap-2">
                <input
                  type="text"
                  name={`nofill_newpw_${Date.now()}`}
                  autoComplete="nope"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 6 chars)"
                  style={{ WebkitTextSecurity: 'disc' }}
                />
                <Button
                  size="sm"
                  variant="secondary"
                  loading={passwordSaving}
                  onClick={handleSetPassword}
                  disabled={!newPassword}
                >
                  Set
                </Button>
              </div>
              <p className="text-xs text-gray-400 mt-1">Password is changed immediately when you click Set.</p>
            </div>
          )}

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={form.is_active ?? true}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              className="rounded border-gray-300 text-blue-600"
            />
            Active
          </label>

          <div className="flex items-center gap-2 pt-3 border-t border-gray-200">
            <Button loading={saving} onClick={handleSave}>{creating ? 'Create User' : 'Save'}</Button>
            <Button variant="ghost" onClick={closePanel}>Cancel</Button>
          </div>
        </div>
      </SlideOutPanel>
    </div>
  );
}
